import { createAsyncThunk } from '@reduxjs/toolkit';

import { clearCaptions, setCaptionsEnabled } from './callCaptionsSlice';
import {
  startCaptionsSessionThunk,
  stopCaptionsSessionThunk,
} from './callCaptionsSessionThunks';
import { selectCaptionsEnabled } from './selectors';

import type { AppDispatch, RootState } from 'features/app/store/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { selectIsConnected } from 'features/call/call-ui-state/store';
import { voiceSessionIdSelector } from 'features/voice-session/store';

const SETTINGS_TOGGLE_SOURCE = 'settings-toggle';

const logCaptionsToggle = (
  dispatch: AppDispatch,
  message: 'Captions enabled' | 'Captions disabled',
  state: RootState
) => {
  dispatch(
    sendAnalyticsInfo({
      Method: 'toggleCaptionsThunk',
      Message: message,
      properties:
        message === 'Captions enabled'
          ? {
              voiceSessionId: voiceSessionIdSelector(state),
              isCallConnected: selectIsConnected(state),
            }
          : { voiceSessionId: voiceSessionIdSelector(state) },
    })
  );
};

const disableCaptionsFromToggle = async (
  dispatch: AppDispatch,
  state: RootState
) => {
  logCaptionsToggle(dispatch, 'Captions disabled', state);
  dispatch(setCaptionsEnabled(false));
  dispatch(clearCaptions());

  try {
    await dispatch(
      stopCaptionsSessionThunk({ source: SETTINGS_TOGGLE_SOURCE })
    ).unwrap();
  } catch {
    // Keep captions disabled so ingestion stays gated.
  }
};

const enableCaptionsFromToggle = async (
  dispatch: AppDispatch,
  state: RootState
) => {
  logCaptionsToggle(dispatch, 'Captions enabled', state);
  dispatch(setCaptionsEnabled(true));

  // Start lives here — not in middleware on setCaptionsEnabled — so a failed
  // start API can revert the toggle without racing a second start.
  try {
    await dispatch(
      startCaptionsSessionThunk({ source: SETTINGS_TOGGLE_SOURCE })
    ).unwrap();
  } catch {
    dispatch(setCaptionsEnabled(false));
  }
};

export const toggleCaptionsThunk = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>('callCaptions/toggle', async (_void, { dispatch, getState }) => {
  const state = getState();

  if (selectCaptionsEnabled(state)) {
    await disableCaptionsFromToggle(dispatch, state);
    return;
  }

  await enableCaptionsFromToggle(dispatch, state);
});
